import { CommonModule } from "@angular/common";
import { Component, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRouteSnapshot, NavigationEnd, Router, RouterModule } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { MenuItem } from "primeng/api";
import { Menu, MenuModule } from "primeng/menu";

import { BehaviorSubject, filter, map } from "rxjs";

interface Breadcrumb {
  label: string;
  url?: string;
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: "[app-breadcrumb]",
  standalone: true,
  imports: [CommonModule, RouterModule, MenuModule],
  template: `<nav class="layout-breadcrumb h-full text-sm md:text-base">
    <ol class="gap-2!">
      <!-- Mobile: Show dropdown button if there are hidden breadcrumbs -->
      @if ((hiddenBreadcrumbs$ | async)?.length) {
        <li class="breadcrumb-dropdown-trigger">
          <button
            type="button"
            class="breadcrumb-link cursor-pointer border-none bg-transparent pl-4 pr-1"
            (click)="breadcrumbMenu.toggle($event)"
          >
            ...
          </button>
          <span class="layout-breadcrumb-chevron ml-2">></span>
          <p-menu #breadcrumbMenu [model]="(menuItems$ | async) ?? []" [popup]="true" />
        </li>
      }

      <!-- Desktop: Show all breadcrumbs -->
      @if (breadcrumbs$ | async; as breadcrumbs) {
        @for (item of breadcrumbs; track item; let last = $last; let first = $first) {
          <li
            [class.breadcrumb-item]="true"
            [class.breadcrumb-last]="last"
            [class]="breadcrumbs.length > 1 ? '' : 'ml-3 md:ml-1'"
          >
            @if (!last) {
              <a [routerLink]="item.url" class="breadcrumb-link">{{ item.label }}</a>
            } @else {
              <span>{{ item.label }}</span>
            }
          </li>
          @if (!last) {
            <li class="layout-breadcrumb-chevron">></li>
          }
        }
      }
    </ol>
  </nav> `,
  // We have to use inline styles here because something is constantly overriding the styles. I can't figure out what it is (and it is not _breadcrumb.scss as you might think).
  styles: [
    `
      :host {
        display: block;
      }

      .layout-breadcrumb {
        display: flex;
        justify-content: start;

        ol {
          display: flex;
          align-items: center;
          margin: 0;
          padding: 0;
          list-style: none;
          flex-wrap: wrap;
          color: var(--text-color-secondary);

          li {
            font-weight: 500;
          }
        }

        .breadcrumb-link {
          text-decoration: underline;
          color: inherit;
          cursor: pointer;
          transition: opacity 0.2s ease-in-out;

          &:hover {
            opacity: 0.8;
          }
        }
      }

      /* Desktop: Hide the dropdown trigger */
      @media screen and (min-width: 769px) {
        .breadcrumb-dropdown-trigger {
          display: none;
        }
      }

      /* Mobile: Hide all breadcrumbs except the last one, show dropdown trigger */
      @media screen and (max-width: 768px) {
        .layout-breadcrumb {
          ol {
            li.breadcrumb-item:not(.breadcrumb-last),
            li.breadcrumb-item:not(.breadcrumb-last) + li.layout-breadcrumb-chevron {
              display: none;
            }

            li.breadcrumb-dropdown-trigger {
              display: flex;
              align-items: center;
            }
          }
        }
      }
    `,
  ],
})
export class AppBreadcrumbComponent {
  @ViewChild("breadcrumbMenu") breadcrumbMenu!: Menu;

  private readonly _breadcrumbs$ = new BehaviorSubject<Breadcrumb[]>([]);

  readonly breadcrumbs$ = this._breadcrumbs$.asObservable();

  // Hidden breadcrumbs (all except the last one) for mobile dropdown
  readonly hiddenBreadcrumbs$ = this.breadcrumbs$.pipe(
    map((breadcrumbs) => {
      if (breadcrumbs.length <= 1) {
        return [];
      }
      return breadcrumbs.slice(0, -1);
    }),
  );

  readonly menuItems$ = this.hiddenBreadcrumbs$.pipe(
    map((breadcrumbs) =>
      breadcrumbs.map(
        (breadcrumb): MenuItem => ({
          label: breadcrumb.label,
          command: () => {
            if (breadcrumb.url) {
              this.router.navigate([breadcrumb.url]);
            }
          },
        }),
      ),
    ),
  );

  constructor(
    private router: Router,
    private translocoService: TranslocoService,
  ) {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const root = this.router.routerState.snapshot.root;
        const breadcrumbs: Breadcrumb[] = [];
        this.addBreadcrumb(root, [], breadcrumbs);

        this._breadcrumbs$.next(breadcrumbs);
      });
  }

  private addBreadcrumb(route: ActivatedRouteSnapshot, parentUrl: string[], breadcrumbs: Breadcrumb[]) {
    const routeUrl = parentUrl.concat(route.url.map((url) => url.path));
    const label = this._breadcrumbLabel(route);
    const parentLabel = route.parent ? this._breadcrumbLabel(route.parent) : null;

    if (label && label !== parentLabel) {
      breadcrumbs.push({
        label,
        url: "/" + routeUrl.join("/"),
      });
    }

    if (route.firstChild) {
      this.addBreadcrumb(route.firstChild, routeUrl, breadcrumbs);
    }
  }

  /**
   * Resolves a route's breadcrumb label: a resolved `breadcrumbLabel` (already localized, e.g. a service name)
   * takes precedence over the static `breadcrumb` i18n key.
   *
   * t(app.breadcrumbs.featureDetails)
   * t(app.breadcrumbs.processDetails)
   * t(app.breadcrumbs.processForm)
   * t(app.breadcrumbs.serviceDetails)
   * t(app.breadcrumbs.userDetails)
   * t(app.sidebar.pages.extraServiceDetails)
   * t(app.uploadedFiles.breadcrumb)
   */
  private _breadcrumbLabel(route: ActivatedRouteSnapshot): string | null {
    const resolvedLabel = route.data["breadcrumbLabel"];
    if (typeof resolvedLabel === "string" && resolvedLabel.length > 0) {
      return resolvedLabel;
    }
    const breadcrumbKey = route.data["breadcrumb"];
    return breadcrumbKey ? this.translocoService.translate(breadcrumbKey) : null;
  }
}
